import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');
const path = (orgId: string) => `/api/v1/organizations/${orgId}/uat/crm-new-credential/inspect`;
const existingLoftyPath = '/api/v1/organizations/816d1e20-f38b-466e-96ec-46bb7d0f404c/uat/crm-existing-lofty/inspect';
const loftyActionPath = '/api/v1/organizations/816d1e20-f38b-466e-96ec-46bb7d0f404c/uat/crm-existing-lofty';
const savedCredentialActionPaths = {
  '6e5adb4f-c2b4-459b-8c40-f41bb801bbf2': '/api/v1/organizations/6e5adb4f-c2b4-459b-8c40-f41bb801bbf2/uat/crm-saved-credential',
  '048164c9-68a7-4f2c-ac30-3400e3fa47e7': '/api/v1/organizations/048164c9-68a7-4f2c-ac30-3400e3fa47e7/uat/crm-saved-credential',
} as const;
export type UatSavedCredentialOrgId = keyof typeof savedCredentialActionPaths;

export type ProtectedApp = 'lofty' | 'sierra-interactive' | 'boldtrail';
export interface UatCrmInspection {
  status: 'not_qualified';
  reason: 'provider_account_field_unreviewed';
  organization_id: string;
  app_key: ProtectedApp;
  read_only: true;
  installed: false;
  writes: 0;
  metadata: {
    connector_found: true;
    plans: Array<{ plan: string | null; auth_status: 'recognized' | 'missing' | 'unrecognized'; auth_type: 'api_token' | 'oauth1' | 'oauth2' | null }>;
    read_capabilities: Array<{ entity: 'people' | 'users' | 'notes' | 'tasks' | 'transactions'; list_supported: boolean }>;
    credential_present: boolean;
    credential_valid: boolean;
    credential_plan: string | null;
    profile_paths: Array<{ path: string; type: 'object' | 'array' | 'boolean' | 'number' | 'string' | 'null' | 'other' }>;
  };
}

export async function inspectUatConnector(orgId: string): Promise<UatCrmInspection> {
  return (await remiApi.get<UatCrmInspection>(path(orgId))).data;
}

/** Read only: the server discovers the existing saved Lofty credential. */
export async function inspectUatExistingLofty(): Promise<UatCrmInspection> {
  return (await remiApi.get<UatCrmInspection>(existingLoftyPath)).data;
}

export interface UatLoftyExactPersonInspection {
  status: 'exact_fixture_read';
  credential_bound: boolean;
  provider_account_qualified: false;
  import_enabled: false;
  person_id_match: true;
  synthetic_email: true;
  phone_absent: true;
  names_present: true;
  writes: 0;
}

export interface UatLoftyDisabledStatus {
  status: 'not_installed' | 'credential_bound_disabled';
  install_admitted: boolean;
  plan: string;
  auth_type: string;
  provider_account_qualified: false;
  import_enabled: false;
  outreach_enabled: false;
  writes: 0;
  replayed?: boolean;
}

/** Both reads use server-fixed identity and return only bounded safety fields. */
export async function inspectUatExactLoftyPerson(): Promise<UatLoftyExactPersonInspection> {
  return (await remiApi.get<UatLoftyExactPersonInspection>(`${loftyActionPath}/exact-person`)).data;
}

export async function getUatLoftyDisabledStatus(): Promise<UatLoftyDisabledStatus> {
  return (await remiApi.get<UatLoftyDisabledStatus>(`${loftyActionPath}/disabled-status`)).data;
}

/** Requires a separately enabled server-side admission gate; sends no body. */
export async function installUatLoftyDisabledBinding(): Promise<UatLoftyDisabledStatus> {
  return (await remiApi.post<UatLoftyDisabledStatus>(`${loftyActionPath}/install-disabled`)).data;
}

export interface UatSavedCredentialDisabledStatus {
  status: 'not_saved' | 'not_installed' | 'credential_bound_disabled';
  app_key: 'sierra-interactive' | 'boldtrail';
  plan: string | null;
  auth_type: string | null;
  install_admitted: boolean;
  provider_account_qualified: false;
  import_enabled: false;
  outreach_enabled: false;
  writes: 0;
  replayed?: boolean;
}

export interface UatSavedCredentialCandidateInspection {
  status: 'candidate_read_verified';
  credential_bound: boolean;
  candidate_id_match: true;
  synthetic_shape_match: true;
  provider_account_qualified: false;
  import_enabled: false;
  outreach_enabled: false;
  writes: 0;
}

/** Fixed Sierra/Bold targets; callers cannot supply an app, credential or person ID. */
export async function getUatSavedCredentialDisabledStatus(
  orgId: UatSavedCredentialOrgId,
): Promise<UatSavedCredentialDisabledStatus> {
  return (await remiApi.get<UatSavedCredentialDisabledStatus>(
    `${savedCredentialActionPaths[orgId]}/disabled-status`)).data;
}

export async function inspectUatSavedCredentialCandidate(
  orgId: UatSavedCredentialOrgId,
): Promise<UatSavedCredentialCandidateInspection> {
  return (await remiApi.get<UatSavedCredentialCandidateInspection>(
    `${savedCredentialActionPaths[orgId]}/candidate-person`)).data;
}

/** Server gate controls this empty-body disabled binding action. */
export async function installUatSavedCredentialDisabledBinding(
  orgId: UatSavedCredentialOrgId,
): Promise<UatSavedCredentialDisabledStatus> {
  return (await remiApi.post<UatSavedCredentialDisabledStatus>(
    `${savedCredentialActionPaths[orgId]}/install-disabled`)).data;
}

export async function inspectUatSavedCredential(orgId: string, credentialId: string): Promise<UatCrmInspection> {
  return (await remiApi.post<UatCrmInspection>(path(orgId), { credential_id: credentialId })).data;
}
